import {
  fieldOptionDtoSchema,
  fieldValueDtoSchema,
  type FieldDto,
  type FieldOptionDto,
  type FieldValueDto,
  type SignatureValueDto,
} from '../dto/envelope.dto.js'
import type { FieldType, PrismaFieldType } from '../enums/field-type.enum.js'

export type { FieldOptionDto, FieldValueDto, SignatureValueDto }

type FieldOf<T extends FieldType> = Extract<FieldDto, { type: T }>

/**
 * A field paired with the draft value that actually fits its type.
 * A draft of the wrong shape (a string on a checkbox, a boolean on a
 * text field) is dropped, so each arm can be used without a cast.
 */
export type BoundField =
  | { type: 'checkbox'; field: FieldOf<'checkbox'>; value: boolean | undefined }
  | { type: 'signature'; field: FieldOf<'signature'>; value: SignatureValueDto | undefined }
  | { type: 'initials'; field: FieldOf<'initials'>; value: SignatureValueDto | undefined }
  | { type: 'radio'; field: FieldOf<'radio'>; value: string | undefined }
  | { type: 'dropdown'; field: FieldOf<'dropdown'>; value: string | undefined }
  | { type: 'text'; field: FieldOf<'text'>; value: string | undefined }
  | { type: 'date'; field: FieldOf<'date'>; value: string | undefined }
  | { type: 'date_signed'; field: FieldOf<'date_signed'>; value: string | undefined }

/** JSON stored on field.value / field.options (Prisma Json). */
export type StoredJson =
  | string
  | number
  | boolean
  | null
  | readonly StoredJson[]
  | { readonly [key: string]: StoredJson | undefined }

export function parseFieldValue(value: StoredJson): FieldValueDto | null {
  if (value == null) {
    return null
  }
  const parsed = fieldValueDtoSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

export function parseFieldOptions(value: StoredJson): FieldOptionDto[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  const options: FieldOptionDto[] = []
  for (const item of value) {
    const parsed = fieldOptionDtoSchema.safeParse(item)
    if (parsed.success) {
      options.push(parsed.data)
    }
  }
  return options.length > 0 ? options : null
}

export function parseCheckboxValue(value: StoredJson): boolean | null {
  return typeof value === 'boolean' ? value : null
}

export function parseTextValue(value: StoredJson): string | null {
  return typeof value === 'string' ? value : null
}

export function isSignatureValue(value: FieldValueDto): value is SignatureValueDto {
  return typeof value === 'object'
}

export function parseSignatureValue(value: StoredJson): SignatureValueDto | null {
  const parsed = parseFieldValue(value)
  if (parsed && isSignatureValue(parsed)) {
    return parsed
  }
  if (typeof parsed === 'string' && parsed.trim().length > 0) {
    return { kind: 'type', data: parsed }
  }
  return null
}

/**
 * A Prisma field's JSON narrowed to the value that type can hold.
 * `value` is untyped JSON, so a checkbox can hold a string; a value of the
 * wrong shape is dropped and each arm can be drawn without a cast. A
 * signature stored as a plain string (older rows) is read as typed ink.
 */
export type BoundStoredField =
  | { type: 'checkbox'; value: boolean | null }
  | { type: 'radio'; value: string | null }
  | { type: 'signature'; value: SignatureValueDto | null }
  | { type: 'initials'; value: SignatureValueDto | null }
  | { type: 'dropdown'; value: string | null; options: FieldOptionDto[] | null }
  | { type: 'text'; value: string | null }
  | { type: 'date'; value: string | null }
  | { type: 'date_signed'; value: string | null }

function storedText(value: StoredJson): string | null {
  const parsed = parseFieldValue(value)
  return typeof parsed === 'string' ? parsed : null
}

function storedBoolean(value: StoredJson): boolean | null {
  const parsed = parseFieldValue(value)
  return typeof parsed === 'boolean' ? parsed : null
}

/** `type` is the Prisma enum (`CHECKBOX`), not the wire name (`checkbox`). */
export function bindStoredField(
  type: PrismaFieldType,
  value: StoredJson,
  options: StoredJson = null,
): BoundStoredField {
  switch (type) {
    case 'CHECKBOX':
      return { type: 'checkbox', value: storedBoolean(value) }
    case 'RADIO':
      return { type: 'radio', value: storedText(value) }
    case 'SIGNATURE':
      return { type: 'signature', value: parseSignatureValue(value) }
    case 'INITIALS':
      return { type: 'initials', value: parseSignatureValue(value) }
    case 'DROPDOWN':
      return { type: 'dropdown', value: storedText(value), options: parseFieldOptions(options) }
    case 'TEXT':
      return { type: 'text', value: storedText(value) }
    case 'DATE':
      return { type: 'date', value: storedText(value) }
    case 'DATE_SIGNED':
      return { type: 'date_signed', value: storedText(value) }
  }
}

export function valueFitsType(type: FieldDto['type'], value: FieldValueDto): boolean {
  switch (type) {
    case 'checkbox':
      return typeof value === 'boolean'
    case 'signature':
    case 'initials':
      return isSignatureValue(value)
    case 'radio':
    case 'dropdown':
    case 'text':
    case 'date':
    case 'date_signed':
      return typeof value === 'string'
  }
}

function signatureDraft(raw: FieldValueDto | null | undefined): SignatureValueDto | undefined {
  return raw != null && isSignatureValue(raw) ? raw : undefined
}

function textDraft(raw: FieldValueDto | null | undefined): string | undefined {
  return typeof raw === 'string' ? raw : undefined
}

/** The draft value narrowed to `field`'s type, or omitted when it does not fit. */
export function bindFieldValue(
  field: FieldDto,
  raw: FieldValueDto | null | undefined,
): BoundField {
  switch (field.type) {
    case 'checkbox':
      return { type: field.type, field, value: typeof raw === 'boolean' ? raw : undefined }
    case 'signature':
      return { type: field.type, field, value: signatureDraft(raw) }
    case 'initials':
      return { type: field.type, field, value: signatureDraft(raw) }
    case 'radio':
      return { type: field.type, field, value: textDraft(raw) }
    case 'dropdown':
      return { type: field.type, field, value: textDraft(raw) }
    case 'text':
      return { type: field.type, field, value: textDraft(raw) }
    case 'date':
      return { type: field.type, field, value: textDraft(raw) }
    case 'date_signed':
      return { type: field.type, field, value: textDraft(raw) }
  }
}

export function fieldValuePresent(value: FieldValueDto | null | undefined): boolean {
  if (value == null) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (typeof value === 'boolean') {
    return true
  }
  return value.data.trim().length > 0
}
